import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { deleteFlight, fetchAllFlights, fetchFlights } from '../../features/admin/flightSlice';
import ConfirmDeleteModal from './models/ConfirmDeleteModal';
import InnerLoading from '../ui/InnerLoading';
import { FaPlaneDeparture, FaPlaneArrival, FaMoneyBillWave, FaCalendarAlt, FaTicketAlt, FaEdit, FaTrashAlt } from 'react-icons/fa';
import { Flight } from '../../types';
import FlightModal from './models/FlightModal';
import { FaGlobe } from 'react-icons/fa6';


const FlightPage = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();
	const { allFlights, loading } = useSelector((state: RootState) => state.flightSlice);

	const [modalOpen, setModalOpen] = useState(false);
	const [editFlight, setEditFlight] = useState<Flight | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedFlightId, setSelectedFlightId] = useState<number | null>(null);

	useEffect(() => {
		dispatch(fetchAllFlights());
	}, [dispatch]);

	const handleDelete = async () => {
		if (selectedFlightId !== null) {
			await dispatch(deleteFlight(selectedFlightId));
			dispatch(fetchFlights());
			setSelectedFlightId(null);
		}
	};

	const handleEdit = (Flight: Flight) => {
		setEditFlight(Flight);
		setModalOpen(true);
	};

	const handleAdd = () => {
		setEditFlight(null);
		setModalOpen(true);
	};

	const confirmDelete = (id: number) => {
		setSelectedFlightId(id);
		setDeleteModalOpen(true);
	};


	return (
		<div className="relative p-4 bg-white flex-center rounded-xl shadow-md my-20 main-prop flex flex-col">
			<div className='absolute top-0 -translate-y-1/2 w-[98%] bg-gradient-to-t from-gray-600 to-textSecond rounded-lg shadow-md p-7'>
				<h2 className='text-white text-lg font-semibold'>{t("Flight-title")}</h2>
			</div>
			{loading ? (
				<InnerLoading />
			) : allFlights.length === 0 ? (
				<p className="text-center text-gray-500">{t("no-Flights")}</p>
			) : (<div className=' grid w-full gap-5'>
				{allFlights.map((flight: Flight) => (
					<div key={flight.id} className="bg-white rounded-2xl shadow-md p-4 space-y-4 w-full  border border-gray-200">
						<div >
							<div className="flex items-center gap-2">
								<div className=' w-10 h-10 rounded-full ring-1 flex-center overflow-hidden'>
									<img src={flight.companyImage} alt="Company" className="w-full  " />
								</div>
								<span className="font-bold text-lg">{flight.tripTicket}</span>
							</div>
						</div>
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-700">
							<div>
								<FaPlaneDeparture className="inline me-1 text-blue-600" />
								<strong>{t('from')}:</strong> {flight.fromCountry}
							</div>
							<div>
								<FaPlaneArrival className="inline me-1 text-green-600" />
								<strong>{t('to')}:</strong> {flight.toCountry}
							</div>
							<div>
								<FaCalendarAlt className="inline me-1 text-purple-600" />
								<strong>{t('trip-date')}:</strong> {new Date(flight.date).toLocaleDateString()}
							</div>
							<div>
								<FaTicketAlt className="inline me-1 text-orange-600" />
								<strong>{t('class')}:</strong> {flight.classType}
							</div>
							<div>
								<FaMoneyBillWave className="inline me-1 text-green-700" />
								<strong>{t('price')}:</strong> {flight.price} {flight.currencyCode}
							</div>
							<div>
								<FaGlobe className="inline me-1 text-gray-600" />
								<strong>{t('status')}:</strong> {flight.inTrip ? t('in-trip') : t('not-active')}
							</div>
						</div>
						<div className="text-sm text-gray-500 flex justify-between">
							<span>{t('departure')}: {flight.departureTime}</span>
							<span>{t('arrival')}: {flight.arriveTime}</span>
						</div>
						<div className="flex justify-end gap-2 mt-4">
							<button
								onClick={() => handleEdit(flight)}
								className="flex items-center gap-1 text-sm px-3 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded">
								<FaEdit /> {t("edit")}
							</button>
							<button
								onClick={() => confirmDelete(flight.id)}
								className="flex items-center gap-1 text-sm px-3 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded"
							>
								<FaTrashAlt /> {t("delete")}
							</button>
						</div>
					</div>
				))}
			</div>
			)}
			<div className="flex justify-end w-full pt-6">
				<button onClick={handleAdd} className="bg-primary px-6 py-2 rounded-full text-white hover:bg-darkColor transition">
					+ {t("add-new-Flight")}
				</button>
			</div>
			{modalOpen && (<FlightModal onClose={() => setModalOpen(false)} onSuccess={() => dispatch(fetchFlights())} initialData={editFlight} />)}
			<ConfirmDeleteModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleDelete} messageKey="delete-Flight-confirm" />
		</div>
	);
};
export default FlightPage;